import { useState, useRef, useCallback, useEffect } from 'react';

interface RealtimeConfig {
  conversationId?: string;
  onTranscriptUpdate?: (userText: string, assistantText: string) => void;
  onError?: (error: Error) => void;
  onConnect?: () => void;
  onDisconnect?: () => void;
}

export const useOpenAIRealtime = (config: RealtimeConfig) => {
  const [isConnected, setIsConnected] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [userTranscript, setUserTranscript] = useState('');
  const [assistantTranscript, setAssistantTranscript] = useState('');
  
  const peerConnectionRef = useRef<RTCPeerConnection | null>(null);
  const dataChannelRef = useRef<RTCDataChannel | null>(null);
  const audioElementRef = useRef<HTMLAudioElement | null>(null);
  const mediaStreamRef = useRef<MediaStream | null>(null);
  const currentUserTranscriptRef = useRef<string>(''); // Store current turn's user transcript

  const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';

  // Initialize audio element for playback
  useEffect(() => {
    audioElementRef.current = new Audio();
    audioElementRef.current.autoplay = true;
    
    return () => {
      if (audioElementRef.current) {
        audioElementRef.current.pause();
        audioElementRef.current.srcObject = null;
      }
    };
  }, []);

  const connect = useCallback(async () => {
    try {
      setError(null);
      console.log('[OpenAI Realtime] Starting connection...');
      
      // Get ephemeral token from backend
      const token = localStorage.getItem('token');
      console.log('[OpenAI Realtime] Fetching session token...');
      const response = await fetch(`${apiUrl}/api/ai/realtime/session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          conversationId: config.conversationId
        })
      });

      const data = await response.json();
      console.log('[OpenAI Realtime] Session response:', data);
      
      if (!data.success) {
        throw new Error(data.message || 'Failed to create session');
      }

      const { clientSecret } = data.data;
      console.log('[OpenAI Realtime] Got client secret, creating WebRTC connection...');

      // Create RTCPeerConnection
      const pc = new RTCPeerConnection();
      peerConnectionRef.current = pc;

      // Set up error handlers
      pc.onicecandidateerror = (event) => {
        console.error('[OpenAI Realtime] ICE candidate error:', event);
      };

      pc.onconnectionstatechange = () => {
        console.log('[OpenAI Realtime] Connection state:', pc.connectionState);
        if (pc.connectionState === 'failed' || pc.connectionState === 'disconnected') {
          setIsConnected(false);
          config.onDisconnect?.();
        }
      };

      pc.oniceconnectionstatechange = () => {
        console.log('[OpenAI Realtime] ICE connection state:', pc.iceConnectionState);
      };

      // Set up audio playback
      pc.ontrack = (event) => {
        console.log('[OpenAI Realtime] Received remote track');
        if (audioElementRef.current && event.streams[0]) {
          audioElementRef.current.srcObject = event.streams[0];
        }
      };

      // Add microphone track
      console.log('[OpenAI Realtime] Requesting microphone access...');
      const ms = await navigator.mediaDevices.getUserMedia({ 
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true
        } 
      });
      console.log('[OpenAI Realtime] Microphone access granted');
      mediaStreamRef.current = ms;
      
      ms.getTracks().forEach(track => {
        pc.addTrack(track, ms);
      });

      // Set up data channel for text events
      const dc = pc.createDataChannel('oai-events');
      dataChannelRef.current = dc;

      dc.addEventListener('open', () => {
        console.log('[OpenAI Realtime] Data channel opened, waiting for session.created...');
        // Don't send session.update - session is already configured by backend
        // Just wait for session.created confirmation
      });

      dc.addEventListener('message', (event) => {
        try {
          const message = JSON.parse(event.data);
          console.log('[OpenAI Realtime] <<<< Received message:', message.type);
          console.log('[OpenAI Realtime] Full message:', JSON.stringify(message, null, 2));

          switch (message.type) {
            case 'session.created':
            case 'session.updated':
              console.log('[OpenAI Realtime] Session ready:', message);
              setIsConnected(true);
              setIsRecording(true); // Start recording immediately
              config.onConnect?.();
              break;
              
            case 'input_audio_buffer.speech_started':
              console.log('[OpenAI Realtime] User started speaking');
              setIsRecording(true);
              break;
              
            case 'input_audio_buffer.speech_stopped':
              console.log('[OpenAI Realtime] User stopped speaking');
              setIsRecording(false);
              break;
              
            case 'conversation.item.input_audio_transcription.completed':
              console.log('[OpenAI Realtime] User transcript:', message.transcript);
              const userText = message.transcript;
              setUserTranscript(userText);
              currentUserTranscriptRef.current = userText; // Store in ref
              // Send user message immediately to chat
              if (userText && config.onTranscriptUpdate) {
                console.log('[OpenAI Realtime] Sending user message to chat:', userText);
                config.onTranscriptUpdate(userText, '');
              }
              break;
              
            case 'response.audio_transcript.delta':
              const transcriptDelta = message.delta;
              console.log('[OpenAI Realtime] Assistant transcript delta:', transcriptDelta);
              setAssistantTranscript(prev => prev + transcriptDelta);
              break;
              
            case 'response.audio_transcript.done':
              console.log('[OpenAI Realtime] Assistant transcript complete:', message.transcript);
              const fullTranscript = message.transcript;
              setAssistantTranscript(fullTranscript);
              // Send assistant message to chat
              if (fullTranscript && config.onTranscriptUpdate) {
                console.log('[OpenAI Realtime] Sending assistant message to chat:', fullTranscript);
                config.onTranscriptUpdate('', fullTranscript);
              }
              // Save both transcripts to backend
              const savedUserText = currentUserTranscriptRef.current;
              if (savedUserText && fullTranscript && config.conversationId) {
                console.log('[OpenAI Realtime] Saving transcripts to backend');
                saveTranscript(savedUserText, fullTranscript);
              }
              // Reset for next turn
              setUserTranscript('');
              setAssistantTranscript('');
              currentUserTranscriptRef.current = '';
              break;
              
            case 'response.audio.delta':
              setIsSpeaking(true);
              break;
              
            case 'response.audio.done':
              console.log('[OpenAI Realtime] Audio playback done');
              setIsSpeaking(false);
              break;
              
            case 'response.done':
              console.log('[OpenAI Realtime] Response complete');
              setIsRecording(false);
              setIsSpeaking(false);
              break;
              
            case 'error':
              console.error('[OpenAI Realtime] Error event:', message.error);
              setError(message.error.message || 'An error occurred');
              config.onError?.(new Error(message.error.message));
              break;
              
            default:
              console.log('[OpenAI Realtime] Unhandled message type:', message.type);
          }
        } catch (err) {
          console.error('[OpenAI Realtime] Failed to parse message:', err);
        }
      });

      dc.addEventListener('close', (event) => {
        console.log('[OpenAI Realtime] Data channel closed:', event);
        setIsConnected(false);
        setIsRecording(false);
        setIsSpeaking(false);
        config.onDisconnect?.();
      });

      dc.addEventListener('error', (event) => {
        console.error('[OpenAI Realtime] Data channel error:', event);
        const errorMsg = 'Data channel error occurred';
        setError(errorMsg);
        config.onError?.(new Error(errorMsg));
      });

      // Create and set local description
      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);

      // Send offer to OpenAI Realtime API
      console.log('[OpenAI Realtime] Sending SDP offer to OpenAI...');
      const sdpResponse = await fetch('https://api.openai.com/v1/realtime', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${clientSecret}`,
          'Content-Type': 'application/sdp'
        },
        body: offer.sdp
      });

      console.log('[OpenAI Realtime] SDP response status:', sdpResponse.status);

      if (!sdpResponse.ok) {
        const errorText = await sdpResponse.text();
        console.error('[OpenAI Realtime] SDP error:', errorText);
        throw new Error(`Failed to connect to OpenAI Realtime API: ${sdpResponse.status} ${errorText}`);
      }

      const answer = {
        type: 'answer' as RTCSdpType,
        sdp: await sdpResponse.text()
      };

      console.log('[OpenAI Realtime] Setting remote description...');
      await pc.setRemoteDescription(answer);
      console.log('[OpenAI Realtime] WebRTC connection established!');

    } catch (err) {
      console.error('[OpenAI Realtime] Connection error:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to connect';
      setError(errorMessage);
      config.onError?.(err instanceof Error ? err : new Error(errorMessage));
      disconnect();
    }
  }, [config, apiUrl, userTranscript, assistantTranscript]);

  const disconnect = useCallback(() => {
    console.log('[OpenAI Realtime] disconnect() called');
    console.trace('[OpenAI Realtime] disconnect stack trace');
    
    // Close data channel
    if (dataChannelRef.current) {
      console.log('[OpenAI Realtime] Closing data channel...');
      dataChannelRef.current.close();
      dataChannelRef.current = null;
    }

    // Close peer connection
    if (peerConnectionRef.current) {
      console.log('[OpenAI Realtime] Closing peer connection...');
      peerConnectionRef.current.close();
      peerConnectionRef.current = null;
    }

    // Stop media stream
    if (mediaStreamRef.current) {
      console.log('[OpenAI Realtime] Stopping media stream...');
      mediaStreamRef.current.getTracks().forEach(track => track.stop());
      mediaStreamRef.current = null;
    }

    // Clean up audio
    if (audioElementRef.current) {
      audioElementRef.current.pause();
      audioElementRef.current.srcObject = null;
    }

    setIsConnected(false);
    setIsRecording(false);
    setIsSpeaking(false);
    setUserTranscript('');
    setAssistantTranscript('');
    
    config.onDisconnect?.();
  }, [config]);

  const saveTranscript = async (userText: string, assistantText: string) => {
    try {
      const token = localStorage.getItem('token');
      await fetch(`${apiUrl}/api/ai/realtime/transcript`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          conversationId: config.conversationId,
          userMessage: userText,
          assistantMessage: assistantText
        })
      });
    } catch (err) {
      console.error('[OpenAI Realtime] Failed to save transcript:', err);
    }
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      // Call disconnect when component unmounts
      console.log('[OpenAI Realtime] Component unmounting, cleaning up...');
      if (peerConnectionRef.current) {
        peerConnectionRef.current.close();
      }
      if (dataChannelRef.current) {
        dataChannelRef.current.close();
      }
      if (mediaStreamRef.current) {
        mediaStreamRef.current.getTracks().forEach(track => track.stop());
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty deps = only run on unmount

  return {
    isConnected,
    isRecording,
    isSpeaking,
    error,
    userTranscript,
    assistantTranscript,
    connect,
    disconnect
  };
};

